import type { CourseChapterDetail, PublicCourseDetail, StudentSession } from './types';

export const STUDENT_SESSION_STORAGE_KEY = 'digitaladbird.student.session';

const DEFAULT_CMS_BACKEND_URL =
  process.env.NODE_ENV === 'production' ? 'https://api.digitaladbird.com' : 'http://localhost:5000';

export const getCmsBackendUrl = () =>
  (process.env.NEXT_PUBLIC_CMS_BACKEND_URL || DEFAULT_CMS_BACKEND_URL).replace(/\/+$/, '');

export const formatCurrency = (amount = 0, currency = 'INR') =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format((amount || 0) / 100);

export const formatDurationLabel = (durationMinutes = 0, fallback = '') => {
  if (!durationMinutes) {
    return fallback || 'Self-paced';
  }

  if (durationMinutes < 60) {
    return `${durationMinutes} min`;
  }

  const hours = Math.floor(durationMinutes / 60);
  const minutes = durationMinutes % 60;
  return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
};

export const buildAssetUrl = (value?: string | null) => {
  if (!value) {
    return '';
  }

  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  return `${getCmsBackendUrl()}${value.startsWith('/') ? value : `/${value}`}`;
};

export const isPreviewLesson = (lesson: {
  isFreeIntro?: boolean;
  visibility?: string;
}) => Boolean(lesson.isFreeIntro || lesson.visibility === 'PREVIEW');

export const getLessonVisibilityMeta = (lesson: {
  unlocked?: boolean;
  isFreeIntro?: boolean;
  visibility?: string;
}) => {
  if (lesson.unlocked) {
    return {
      label: 'Unlocked',
      tone: 'emerald',
      message: 'Ready to watch now.',
    };
  }

  if (lesson.visibility === 'COMING_SOON') {
    return {
      label: 'Coming soon',
      tone: 'slate',
      message: 'This lesson will be published later.',
    };
  }

  if (lesson.visibility === 'LOCKED') {
    return {
      label: 'Locked',
      tone: 'rose',
      message: 'This lesson stays locked even after purchase.',
    };
  }

  if (lesson.visibility === 'PAID_ONLY') {
    return {
      label: 'Paid only',
      tone: 'amber',
      message: 'Unlocks only after successful enrollment or payment.',
    };
  }

  return {
    label: 'Preview',
    tone: 'cyan',
    message: 'Available as a preview lesson.',
  };
};

export const flattenCourseLessons = (chapters: CourseChapterDetail[]) =>
  chapters.flatMap((chapter) => chapter.lessons);

export const findFirstPlayableLesson = (course: PublicCourseDetail) => {
  const unlocked = flattenCourseLessons(course.chapters).find((lesson) => lesson.unlocked);
  if (unlocked) {
    return unlocked;
  }

  return flattenCourseLessons(course.chapters).find((lesson) => isPreviewLesson(lesson)) || null;
};

export const readStudentSession = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const rawValue = window.localStorage.getItem(STUDENT_SESSION_STORAGE_KEY);
  if (!rawValue) {
    return null;
  }

  try {
    return JSON.parse(rawValue) as StudentSession;
  } catch {
    window.localStorage.removeItem(STUDENT_SESSION_STORAGE_KEY);
    return null;
  }
};

export const writeStudentSession = (session: StudentSession | null) => {
  if (typeof window === 'undefined') {
    return;
  }

  if (!session) {
    window.localStorage.removeItem(STUDENT_SESSION_STORAGE_KEY);
    return;
  }

  window.localStorage.setItem(STUDENT_SESSION_STORAGE_KEY, JSON.stringify(session));
};
